import type {
  CoverSheetCommissionFields,
  CoverSheetInteraction,
} from './types';

const EMPTY_COMMISSION_FIELDS: CoverSheetCommissionFields = {
  commissionType: null,
  commissionAmountPercent: null,
  commissionAmountDollar: null,
  commissionAdditionalAmount: null,
  counterpartyCommissionType: null,
  counterpartyCommissionAmountPercent: null,
  counterpartyCommissionAmountDollar: null,
  counterpartyCommissionAdditionalAmount: null,
};

/**
 * `AmountPercent` and `AmountDollar` intentionally carry the same value: core
 * stores one amount and `commissionType` decides how the form renders it.
 */
function getLeaseCommissionFields(
  lease: CoverSheetInteraction,
): CoverSheetCommissionFields {
  const landlord = {
    commissionType: lease.landlordCommissionType ?? null,
    commissionAmountPercent: lease.landlordCommissionValue ?? null,
    commissionAmountDollar: lease.landlordCommissionValue ?? null,
    commissionAdditionalAmount:
      lease.landlordCommissionAdditionalAmount ?? null,
  };
  const tenant = {
    commissionType: lease.tenantCommissionType ?? null,
    commissionAmountPercent: lease.tenantCommissionValue ?? null,
    commissionAmountDollar: lease.tenantCommissionValue ?? null,
    commissionAdditionalAmount: lease.tenantCommissionAdditionalAmount ?? null,
  };

  switch (lease.representation) {
    case 'landlord':
      return { ...EMPTY_COMMISSION_FIELDS, ...landlord };
    case 'tenant':
      return { ...EMPTY_COMMISSION_FIELDS, ...tenant };
    case 'dual':
      return {
        ...landlord,
        counterpartyCommissionType: tenant.commissionType,
        counterpartyCommissionAmountPercent: tenant.commissionAmountPercent,
        counterpartyCommissionAmountDollar: tenant.commissionAmountDollar,
        counterpartyCommissionAdditionalAmount:
          tenant.commissionAdditionalAmount,
      };
    default:
      return EMPTY_COMMISSION_FIELDS;
  }
}

function getListingCommissionFields(
  listing: CoverSheetInteraction,
): CoverSheetCommissionFields {
  // On a listing-only-marketplace listing the offer-side commission is owned by
  // the offer, so it is not surfaced here.
  const isLom = listing.offerExperience === 'lom';

  return {
    commissionType: listing.listingCommissionType ?? null,
    commissionAmountPercent: listing.listingCommissionValue ?? null,
    commissionAmountDollar: listing.listingCommissionValue ?? null,
    commissionAdditionalAmount:
      listing.listingCommissionAdditionalAmount ?? null,
    counterpartyCommissionType: isLom
      ? null
      : (listing.offerCommissionType ?? null),
    counterpartyCommissionAmountPercent: isLom
      ? null
      : (listing.offerCommissionValue ?? null),
    counterpartyCommissionAmountDollar: isLom
      ? null
      : (listing.offerCommissionValue ?? null),
    counterpartyCommissionAdditionalAmount: isLom
      ? null
      : (listing.offerCommissionAdditionalAmount ?? null),
  };
}

function getDirectCommissionFields(
  interaction: CoverSheetInteraction,
): CoverSheetCommissionFields {
  return {
    ...EMPTY_COMMISSION_FIELDS,
    commissionType: interaction.commissionType ?? null,
    commissionAmountPercent: interaction.commissionValue ?? null,
    commissionAmountDollar: interaction.commissionValue ?? null,
    commissionAdditionalAmount: interaction.commissionAdditionalAmount ?? null,
  };
}

/**
 * Dispatches on `type` rather than probing for keys: callers pass a normalized
 * interaction whose derived keys are always present, so key-presence checks
 * would send every interaction down the lease branch.
 */
export function getCommissionFields(
  interaction: CoverSheetInteraction,
): CoverSheetCommissionFields {
  switch (interaction.type) {
    case 'lease':
      return getLeaseCommissionFields(interaction);
    case 'listing':
      return getListingCommissionFields(interaction);
    default:
      return getDirectCommissionFields(interaction);
  }
}
